/**
 * A scheduled meeting has no stored end time, so its slot is taken to be this long. Matches the
 * default the invite mails already assume, and keeps the link alive for a call that starts late.
 */
export const MEETING_JOIN_WINDOW_MS = 30 * 60 * 1000;

export type MeetingJoinWindow = {
	startAt: string | Date;
	endAt?: string | Date | null;
	cancelledAt?: string | Date | null;
	now?: number;
};

/**
 * Someone opening the room before the slot and abandoning it gets the meeting stamped ended without
 * the call ever happening, so an end at or before the start is not a completion and must not retire
 * the join link.
 */
export const isMeetingJoinable = ({ startAt, endAt, cancelledAt, now = Date.now() }: MeetingJoinWindow): boolean => {
	if (cancelledAt) return false;

	const start = new Date(startAt).getTime();
	if (Number.isNaN(start)) return false;

	if (isMeetingCompleted({ startAt, endAt })) return false;

	return now <= start + MEETING_JOIN_WINDOW_MS;
};

type MeetingCompletion = Pick<MeetingJoinWindow, 'startAt' | 'endAt'>;

export const isMeetingCompleted = ({ startAt, endAt }: MeetingCompletion): boolean => {
	if (!endAt) return false;
	const end = new Date(endAt).getTime();
	return !Number.isNaN(end) && end > new Date(startAt).getTime();
};

/** Completed, or never ended but its slot has lapsed. A cancelled meeting never happened, so it is neither. */
export const isMeetingInHistory = ({ startAt, endAt, cancelledAt, now = Date.now() }: MeetingJoinWindow): boolean => {
	if (cancelledAt) return false;
	if (isMeetingCompleted({ startAt, endAt })) return true;
	return now > new Date(startAt).getTime() + MEETING_JOIN_WINDOW_MS;
};
